import { RequestOptions } from '@crossnokaye/typescript-sdk/lib/sdks';

import { GaiaCore } from '@repo/sdk/core';
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  SDKError,
  SDKValidationError,
  UnexpectedClientError,
} from '@repo/sdk/models/errors';
import { Result, unwrapAsync } from '@repo/sdk/types/fp';

import { BACKEND_URL } from '@/constants';

type GaiaFuncWithRequest<TRequest, TResponse> = (
  core: GaiaCore,
  request: TRequest,
  options?: RequestOptions
) => Promise<
  Result<
    TResponse,
    | SDKError
    | SDKValidationError
    | UnexpectedClientError
    | InvalidRequestError
    | RequestAbortedError
    | RequestTimeoutError
    | ConnectionError
  >
>;

type GaiaFuncWithoutRequest<TResponse> = (
  core: GaiaCore,
  options?: RequestOptions
) => Promise<
  Result<
    TResponse,
    | SDKError
    | SDKValidationError
    | UnexpectedClientError
    | InvalidRequestError
    | RequestAbortedError
    | RequestTimeoutError
    | ConnectionError
  >
>;

interface GaiaClient<TRequest, TResponse> {
  gaiaFunc: GaiaFuncWithRequest<TRequest, TResponse> | GaiaFuncWithoutRequest<TResponse>;
  request?: TRequest;
  options?: RequestOptions;
  getAccessToken: () => Promise<string>;
}

export const gaiaClient = async <TRequest, TResponse>({
  gaiaFunc,
  request,
  options,
  getAccessToken,
}: GaiaClient<TRequest, TResponse>) => {
  // Instantiate new Gaia Core to ensure we have a fresh instance of the core object
  const authHeader = `Bearer ${await getAccessToken()}`;
  const gaiaCore = new GaiaCore({
    oauth2HeaderAuthorization: authHeader,
    serverURL: BACKEND_URL.toString(),
  });
  if (request) {
    return unwrapAsync(
      (gaiaFunc as GaiaFuncWithRequest<TRequest, TResponse>)(gaiaCore, request, options)
    );
  }
  return unwrapAsync((gaiaFunc as GaiaFuncWithoutRequest<TResponse>)(gaiaCore, options));
};
